import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, MenuItem, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import Notes from "./header";
import FooterManual from "./footerManual";
import './userPerformaInvoices.css';

const UserPerformaInvoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('date-desc');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const API_BASE_URL = 'https://car.amar.co.jp/api/authorization/api/proforma-invoice';

  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      navigate("/login");
    }
  }, [navigate]);

  const handleMyAccountClick = () => {
    navigate("/myAccount");
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("authToken");
      
      if (!token) {
        throw new Error("Authentication token not found. Please log in again.");
      }

      const response = await fetch(`${API_BASE_URL}/user/all`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch invoices: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      const transformedData = data.map(invoice => ({
        id: invoice.proformaInvoiceId,
        invoiceNo: invoice.proformaInvoiceNo,
        invoiceDate: formatDateForDisplay(invoice.proformaInvoiceDate),
        customerName: invoice.customerName,
        customerEmail: invoice.email,
        customerPhone: invoice.phoneNumber,
        shipToCountry: invoice.countryName,
        shipToPort: invoice.portName,
        vehicleBrand: invoice.carBrandName,
        vehicleModel: invoice.carModelName,
        vehicleYear: invoice.carYear,
        vehicleColor: invoice.carColor,
        engineCC: invoice.engineCC,
        vehicleCost: invoice.carPrice,
        shippingCost: invoice.shippingCost,
        shippingOption: invoice.shippingOption === 'CNF' ? 'C&F' : invoice.shippingOption,
        m3: invoice.m3,
        grandTotal: invoice.grandTotal,
        countryId: invoice.countryId,
        portId: invoice.portId,
        carModelId: invoice.carModelId
      }));

      setInvoices(transformedData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    
    const parts = dateString.split(/[-/]/);
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedInvoice(null);
  };

  // Filter and search logic
  const filteredInvoices = invoices.filter(invoice => {
    const searchMatch = 
      invoice.invoiceNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.customerEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.vehicleBrand.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.vehicleModel.toLowerCase().includes(searchQuery.toLowerCase());
    
    let dateMatch = true;
    if (dateRange.start && dateRange.end) {
      const invoiceDate = new Date(invoice.invoiceDate);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      dateMatch = invoiceDate >= startDate && invoiceDate <= endDate;
    }
    
    return searchMatch && dateMatch;
  });

  // Sort logic
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    switch (sortBy) {
      case 'date-desc':
        return new Date(b.invoiceDate) - new Date(a.invoiceDate);
      case 'date-asc':
        return new Date(a.invoiceDate) - new Date(b.invoiceDate);
      case 'amount-desc':
        return b.grandTotal - a.grandTotal;
      case 'amount-asc':
        return a.grandTotal - b.grandTotal;
      default:
        return 0;
    }
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentInvoices = sortedInvoices.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedInvoices.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Statistics
  const stats = {
    total: invoices.length,
    totalAmount: invoices.reduce((sum, inv) => sum + inv.grandTotal, 0)
  };

  return (
    <Box
      className="upi-container-main"
      sx={{
        position: "relative",
        width: "100%",
      }}
    >
      <Box sx={{ marginBottom: "0%" }}><Notes /></Box>

      <Box
        className="upi-image-container"
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "30vh", sm: "40vh", md: "50vh" },
          backgroundColor: "black",
        }}
      >
        <Box
          component="img"
          src="/image/shutterstock.png"
          alt="shutter"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 0.3,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            top: { xs: "5%", sm: "5%", md: "5%" },
            left: { xs: "3%", sm: "5%", md: "5%" },
            margin: 0,
            color: "white",
            fontSize: { xs: "11px", sm: "13px", md: "15px" },
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            padding: { xs: "4px 8px", sm: "5px 10px", md: "5px 10px" },
            borderRadius: "5px",
          }}
        >
          <Typography
            component="span"
            sx={{
              opacity: 0.5,
              marginRight: { xs: "2px", sm: "5px", md: "5px" },
            }}
          >
            <Link to="/" style={{ fontSize: "inherit", color: "white", textDecoration: "none" }}>
              Home/
            </Link>
          </Typography>

          <Link to="/myAccount" style={{ fontSize: "inherit", color: "white", textDecoration: "none" }}>
            My Account
          </Link>

          / Performa Invoices
        </Box>

        <Box
          className="upi-support-paragraph"
          sx={{
            position: "absolute",
            margin: "auto",
            color: "white",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: { xs: "1.5rem", sm: "2rem", md: "30px" },
            fontFamily: "Roboto",
            fontWeight: "700",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: "10px", sm: "15px", md: "20px" },
            textAlign: "center",
            width: "90%"
          }}
        >
          Performa Invoices
          <button
            onClick={handleMyAccountClick}
            style={{
              backgroundColor: "#EB2329",
              color: "white",
              border: "none",
              padding: window.innerWidth < 600 ? "8px 16px" : window.innerWidth < 960 ? "9px 18px" : "10px 20px",
              fontSize: window.innerWidth < 600 ? "14px" : window.innerWidth < 960 ? "15px" : "16px",
              borderRadius: "8px",
              cursor: "pointer",
              textTransform: "uppercase",
              transition: "background-color 0.3s ease",
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#c41f23")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#EB2329")}
          >
            My Account
          </button>
        </Box>
      </Box>

      {/* MAIN CONTENT CONTAINER WITH OVERLAP */}
      <Box
        sx={{
          position: "relative",
          width: { xs: "95%", sm: "92%", md: "90%" },
          backgroundColor: "#FFFFFF",
          border: { xs: "1px solid #EBEBEB", md: "2px solid #EBEBEB" },
          borderRadius: { xs: "8px", md: "10px" },
          marginLeft: "auto",
          marginRight: "auto",
          marginTop: { xs: "-50px", sm: "-60px", md: "-75px" },
          marginBottom: { xs: "20px", sm: "30px", md: "40px" },
          zIndex: 10,
          pb: { xs: 2, sm: 3, md: 4 }
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
            <CircularProgress sx={{ color: "#eb2329" }} />
          </Box>
        ) : error ? (
          <Box className="upi-error-state">
            <p>Error: {error}</p>
            <button onClick={fetchInvoices} className="upi-btn-retry">Retry</button>
          </Box>
        ) : (
          <>
            {/* Statistics Cards - Compact */}
            <Box className="upi-stats-compact">
              <Box className="upi-stat-item">
                <span className="upi-stat-value">{stats.total}</span>
                <span className="upi-stat-label">Total Invoices</span>
              </Box>
              <Box className="upi-stat-divider" />
              <Box className="upi-stat-item">
                <span className="upi-stat-value">${stats.totalAmount.toLocaleString()}</span>
                <span className="upi-stat-label">Total Amount</span>
              </Box>
            </Box>

            {/* Filters Section - Compact */}
            <Box className="upi-filters-compact">
              <TextField
                select
                label="Sort By"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                size="small"
                className="upi-filter-input"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e9ecef' },
                    '&:hover fieldset': { borderColor: '#eb2329' },
                    '&.Mui-focused fieldset': { borderColor: '#eb2329' },
                  }
                }}
              >
                <MenuItem value="date-desc">Date (Newest First)</MenuItem>
                <MenuItem value="date-asc">Date (Oldest First)</MenuItem>
                <MenuItem value="amount-desc">Amount (High to Low)</MenuItem>
                <MenuItem value="amount-asc">Amount (Low to High)</MenuItem>
              </TextField>

              <TextField
                type="date"
                label="From Date"
                size="small"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="upi-filter-input"
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e9ecef' },
                    '&:hover fieldset': { borderColor: '#eb2329' },
                    '&.Mui-focused fieldset': { borderColor: '#eb2329' },
                  }
                }}
              />

              <TextField
                type="date"
                label="To Date"
                size="small"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="upi-filter-input"
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e9ecef' },
                    '&:hover fieldset': { borderColor: '#eb2329' },
                    '&.Mui-focused fieldset': { borderColor: '#eb2329' },
                  }
                }}
              />

              <TextField
                placeholder="Search invoices..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="upi-filter-input upi-search-input"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e9ecef' },
                    '&:hover fieldset': { borderColor: '#eb2329' },
                    '&.Mui-focused fieldset': { borderColor: '#eb2329' },
                  }
                }}
              />
            </Box>

            {/* Table */}
            {currentInvoices.length > 0 ? (
              <>
                <Box sx={{ padding: '0 20px', overflow: 'hidden' }}>
                  <TableContainer 
                    component={Paper} 
                    className="upi-table-container"
                    sx={{
                      margin: '0 0 20px 0',
                      maxWidth: '100%',
                      overflowX: 'auto'
                    }}
                  >
                    <Table sx={{ minWidth: 650 }} className="upi-table">
                      <TableHead>
                        <TableRow className="upi-table-header">
                          <TableCell className="upi-th">Invoice No</TableCell>
                          <TableCell className="upi-th">Date</TableCell>
                          <TableCell className="upi-th">Customer</TableCell>
                          <TableCell className="upi-th">Vehicle</TableCell>
                          <TableCell className="upi-th">Destination</TableCell>
                          <TableCell className="upi-th" align="right">Vehicle Cost</TableCell>
                          <TableCell className="upi-th" align="right">Shipping</TableCell>
                          <TableCell className="upi-th" align="right">Total</TableCell>
                          <TableCell className="upi-th" align="center">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentInvoices.map((invoice) => (
                          <TableRow key={invoice.id} className="upi-table-row">
                            <TableCell className="upi-td upi-invoice-no">
                              {invoice.invoiceNo}
                            </TableCell>
                            <TableCell className="upi-td">
                              {new Date(invoice.invoiceDate).toLocaleDateString('en-GB')}
                            </TableCell>
                            <TableCell className="upi-td">
                              <Box>
                                <Typography className="upi-customer-name">{invoice.customerName}</Typography>
                                <Typography className="upi-customer-email">{invoice.customerEmail}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell className="upi-td">
                              <Box>
                                <Typography className="upi-vehicle-name">
                                  {invoice.vehicleBrand} {invoice.vehicleModel}
                                </Typography>
                                <Typography className="upi-vehicle-year">{invoice.vehicleYear}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell className="upi-td">
                              <Box>
                                <Typography className="upi-destination-country">{invoice.shipToCountry}</Typography>
                                <Typography className="upi-destination-port">{invoice.shipToPort}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell className="upi-td" align="right">
                              ${invoice.vehicleCost.toLocaleString()}
                            </TableCell>
                            <TableCell className="upi-td" align="right">
                              <Box>
                                <Typography className="upi-shipping-cost">${invoice.shippingCost.toLocaleString()}</Typography>
                                <Chip 
                                  label={invoice.shippingOption} 
                                  size="small" 
                                  className="upi-shipping-chip"
                                />
                              </Box>
                            </TableCell>
                            <TableCell className="upi-td upi-total-cell" align="right">
                              ${invoice.grandTotal.toLocaleString()}
                            </TableCell>
                            <TableCell className="upi-td" align="center">
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleViewInvoice(invoice)}
                                className="upi-view-btn"
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ padding: '0 20px' }}>
                    <Box className="upi-pagination-container" sx={{ margin: '0 0 20px 0' }}>
                      <Typography className="upi-pagination-info">
                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedInvoices.length)} of {sortedInvoices.length} invoices
                      </Typography>
                      <Box className="upi-pagination-buttons">
                        <button
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="upi-pagination-btn"
                        >
                          ← Previous
                        </button>
                        
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => paginate(pageNumber)}
                                className={`upi-pagination-btn ${currentPage === pageNumber ? 'upi-active' : ''}`}
                              >
                                {pageNumber}
                              </button>
                            );
                          } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                            return <span key={pageNumber} className="upi-pagination-ellipsis">...</span>;
                          }
                          return null;
                        })}
                        
                        <button
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="upi-pagination-btn"
                        >
                          Next →
                        </button>
                      </Box>
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Box className="upi-no-data">
                <Box className="upi-no-data-icon">📭</Box>
                <Typography variant="h6">No invoices found</Typography>
                <Typography variant="body2" className="upi-no-data-hint">Try adjusting your filters</Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* View Invoice Modal */}
      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "90vh"
          }
        }}
      >
        {selectedInvoice && (
          <>
            <DialogTitle sx={{ 
              background: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
              color: "white",
              fontWeight: 600,
              fontSize: "1.4rem"
            }}>
              Invoice Details - {selectedInvoice.invoiceNo}
            </DialogTitle>
            
            <DialogContent sx={{ backgroundColor: "#f8f9fa", padding: "30px" }}>
              <Box className="upi-modal-section">
                <Typography className="upi-modal-section-title">Customer Information</Typography>
                <Box className="upi-modal-info-grid">
                  <Box className="upi-modal-info-item">
                    <Typography className="upi-modal-info-label">Name:</Typography>
                    <Typography className="upi-modal-info-value">{selectedInvoice.customerName}</Typography>
                  </Box>
                  <Box className="upi-modal-info-item">
                    <Typography className="upi-modal-info-label">Email:</Typography>
                    <Typography className="upi-modal-info-value">{selectedInvoice.customerEmail}</Typography>
                  </Box>
                  <Box className="upi-modal-info-item">
                    <Typography className="upi-modal-info-label">Phone:</Typography>
                    <Typography className="upi-modal-info-value">{selectedInvoice.customerPhone}</Typography>
                  </Box>
                </Box>
              </Box>

              <Box className="upi-modal-section">
                <Typography className="upi-modal-section-title">Shipping Information</Typography>
                <Box className="upi-modal-info-grid">
                  <Box className="upi-modal-info-item">
                    <Typography className="upi-modal-info-label">Country:</Typography>
                    <Typography className="upi-modal-info-value">{selectedInvoice.shipToCountry}</Typography>
                  </Box>
                  <Box className="upi-modal-info-item">
                    <Typography className="upi-modal-info-label">Port:</Typography>
                    <Typography className="upi-modal-info-value">{selectedInvoice.shipToPort}</Typography>
                  </Box>
                  <Box className="upi-modal-info-item">
                    <Typography className="upi-modal-info-label">Shipping Option:</Typography>
                    <Typography className="upi-modal-info-value">{selectedInvoice.shippingOption}</Typography>
                  </Box>
                  <Box className="upi-modal-info-item">
                    <Typography className="upi-modal-info-label">M3:</Typography>
                    <Typography className="upi-modal-info-value">{selectedInvoice.m3}</Typography>
                  </Box>
                </Box>
              </Box>

              <Box className="upi-modal-section">
                <Typography className="upi-modal-section-title">Vehicle Details</Typography>
                <Box className="upi-modal-info-grid">
                  <Box className="upi-modal-info-item">
                    <Typography className="upi-modal-info-label">Brand:</Typography>
                    <Typography className="upi-modal-info-value">{selectedInvoice.vehicleBrand}</Typography>
                  </Box>
                  <Box className="upi-modal-info-item">
                    <Typography className="upi-modal-info-label">Model:</Typography>
                    <Typography className="upi-modal-info-value">{selectedInvoice.vehicleModel}</Typography>
                  </Box>
                  <Box className="upi-modal-info-item">
                    <Typography className="upi-modal-info-label">Year:</Typography>
                    <Typography className="upi-modal-info-value">{selectedInvoice.vehicleYear}</Typography>
                  </Box>
                  <Box className="upi-modal-info-item">
                    <Typography className="upi-modal-info-label">Color:</Typography>
                    <Typography className="upi-modal-info-value">{selectedInvoice.vehicleColor}</Typography>
                  </Box>
                  <Box className="upi-modal-info-item">
                    <Typography className="upi-modal-info-label">Engine CC:</Typography>
                    <Typography className="upi-modal-info-value">{selectedInvoice.engineCC}</Typography>
                  </Box>
                </Box>
              </Box>

              <Box className="upi-modal-section">
                <Typography className="upi-modal-section-title">Cost Breakdown</Typography>
                <Box className="upi-modal-cost-breakdown">
                  <Box className="upi-modal-cost-row">
                    <Typography>Vehicle Cost:</Typography>
                    <Typography>${selectedInvoice.vehicleCost.toLocaleString()}</Typography>
                  </Box>
                  <Box className="upi-modal-cost-row">
                    <Typography>Shipping Cost ({selectedInvoice.shippingOption}):</Typography>
                    <Typography>${selectedInvoice.shippingCost.toLocaleString()}</Typography>
                  </Box>
                  <Box className="upi-modal-cost-row upi-modal-total">
                    <Typography>Grand Total:</Typography>
                    <Typography>${selectedInvoice.grandTotal.toLocaleString()}</Typography>
                  </Box>
                </Box>
              </Box>
            </DialogContent>

            <DialogActions sx={{ padding: "20px 30px", borderTop: "1px solid #e9ecef" }}>
              <Button 
                onClick={closeModal}
                sx={{
                  color: "#6c757d",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#f8f9fa"
                  }
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <FooterManual />
    </Box>
  );
};

export default UserPerformaInvoices;